/** The turn loop.
 *
 * One shape for every actor. Whoever the kernel will accept a command from is
 * asked for one; a human parks until the UI offers something, a computer plans
 * under an abort signal and a wall-clock budget, and a wandering monster is
 * driven through `enumerateNeutralBattleCommands` because it has no `PlayerId`
 * to own a controller. The command goes to `GameSession.execute` and the events
 * it produced are played before the next actor is asked - so an animation is
 * never racing the state it is meant to be showing.
 *
 * The loop owns no state. It reads the session and writes through it.
 */

import type {
  BattleCommand, ContentRegistry, GameCommand, GameEvent, GameState, HeroId, PlayerId,
} from "@game/core";
import {
  SIDE_ATTACKER, SIDE_DEFENDER, activeStack, enumerateNeutralBattleCommands,
  tacticsSide,
} from "@game/core";
import type { DifficultyProfile, PlayerController } from "@game/ai";
import { chooseBattleCommand, resolveDifficulty } from "@game/ai";
import type { GameSession } from "./store.js";

/** Whoever the kernel will accept a command from right now. Mirrors the
 *  headless runner's own `toAct`, which is not exported. */
export function actorToAct(state: GameState): PlayerId | null {
  if (state.victory.over) return null;
  if (state.pending !== null) return state.pending.playerId;
  const b = state.battle;
  if (b !== null) {
    // One side holds the tactics phase (`BattleState.tacticsHexes` is signed).
    // Searching both owners here used to hand the attacker a fight the defender
    // had won, and - worse - `neutralSideToAct` would then see the ownerless
    // wandering-monster side first, ask it for a command it does not have, and
    // stall the loop the moment a Tactics hero walked onto a neutral.
    if (b.tacticsPhase) return b.heroes[tacticsSide(b)].owner;
    const slot = activeStack(b);
    if (slot < 0) return null;
    const side = (b.stacks.side[slot] === SIDE_DEFENDER ? SIDE_DEFENDER : SIDE_ATTACKER) as 0 | 1;
    return b.heroes[side].owner;
  }
  return state.turn.activePlayer;
}

/** The battle side nobody owns, when it is that side's move. */
export function neutralSideToAct(state: GameState): 0 | 1 | null {
  const b = state.battle;
  if (b === null) return null;
  if (b.tacticsPhase) {
    const side = tacticsSide(b);
    return b.heroes[side].owner === null ? side : null;
  }
  const slot = activeStack(b);
  if (slot < 0) return null;
  const side = (b.stacks.side[slot] === SIDE_DEFENDER ? SIDE_DEFENDER : SIDE_ATTACKER) as 0 | 1;
  return b.heroes[side].owner === null ? side : null;
}

/**
 * The player a command belongs to, or null when nothing in the command or the
 * state attributes it.
 *
 * This is a ROUTING question and only that: it says which seat may be handed
 * the order, never whether the order is legal - the kernel keeps that. It
 * exists because "whose command is this" and "who is acting" are two different
 * questions, and the host was answering the first with the second. A seat that
 * is handed another player's command is an I9 breach, so the answer is read off
 * the command and the state rather than off whoever happens to be moving.
 *
 * It asks what a command CARRIES rather than switching on its type, for two
 * reasons. A quoted command name under `apps/web/src` is what
 * `packages/ui/test/reachability.spec.ts` counts as the input layer being able
 * to issue that command, and a routing table is not a control - an exhaustive
 * switch here would have reported `RESEARCH_SPELL` and `BATTLE_TACTICS_DONE` as
 * wired screens that do not exist. And a command added later carries the same
 * fields as its siblings, so it is attributed without anyone remembering to
 * come back here; one that carries none of them returns null, which leaves
 * `dispatch` on its fallback rather than on a wrong seat.
 *
 * The three lookups are combined with `??` rather than ordered by early return
 * because a named id can fail to resolve: `HIRE_HERO` names a hero DEFINITION,
 * which is not a hero in `state.heroes`, and the town beside it is the answer.
 */
export function commandPlayer(state: GameState, command: GameCommand): PlayerId | null {
  // The command says so itself: END_TURN and the marketplace's trade.
  if ("player" in command) return command.player;
  if ("cmd" in command) return battleCommandPlayer(state, command.cmd);
  // Only the prompt the kernel is actually holding can be answered, so a stale
  // id resolves to nobody rather than to the current prompt's owner.
  if ("id" in command) {
    const p = state.pending;
    return p !== null && p.id === command.id ? p.playerId : null;
  }
  const army = "fromArmy" in command ? command.fromArmy : "army" in command ? command.army : null;
  const byArmy = army === null ? null : state.armies[army]?.owner ?? null;
  const byHero = "hero" in command
    ? state.heroes[command.hero as unknown as HeroId]?.owner ?? null
    : null;
  const byTown = "town" in command ? state.towns[command.town]?.owner ?? null : null;
  return byArmy ?? byHero ?? byTown;
}

/** The side a battle order is issued by: named outright by the four commands
 *  that are the hero's rather than a stack's, and otherwise the acting stack's. */
function battleCommandSide(state: GameState, cmd: BattleCommand): 0 | 1 | null {
  const b = state.battle;
  if (b === null) return null;
  if ("side" in cmd) return cmd.side;
  const slot = cmd.stack;
  if (slot < 0 || slot >= b.stacks.n) return null;
  return b.stacks.side[slot] === SIDE_DEFENDER ? SIDE_DEFENDER : SIDE_ATTACKER;
}

/** Who owns that side, or null for the side nobody owns - a wandering monster
 *  has no seat and no controller, which is why the loop drives it itself. */
function battleCommandPlayer(state: GameState, cmd: BattleCommand): PlayerId | null {
  const side = battleCommandSide(state, cmd);
  if (side === null) return null;
  return state.battle?.heroes[side].owner ?? null;
}

export interface LoopOptions {
  readonly content: ContentRegistry;
  readonly controllers: Readonly<Record<string, PlayerController>>;
  /** Wall-clock milliseconds one computer player-turn may plan for. */
  readonly aiBudgetMs?: number;
  /** Policy for wandering monsters. */
  readonly neutral?: DifficultyProfile;
  /** Play what these events imply, then resolve. The loop waits for it. */
  readonly present?: (events: readonly GameEvent[], state: GameState) => Promise<void> | void;
  /** Called whenever the actor changes, so the shell can lock its panels. */
  readonly onActor?: (actor: PlayerId | null, kind: "human" | "ai" | "neutral" | "none") => void;
  readonly onStop?: (reason: LoopStop) => void;
}

export type LoopStop = "victory" | "aborted" | "stalled";

/** A player-turn that issues this many commands without the turn moving on is
 *  hung, whatever it thinks it is doing. */
const STALL_LIMIT = 4096;

/** How long the loop may hold the thread before handing a frame back. */
const FRAME_BUDGET_MS = 12;

export class TurnLoop {
  private readonly session: GameSession;
  private readonly opts: LoopOptions;
  private abort: AbortController | null = null;
  private running = false;
  private stalls = 0;
  private stallKey = "";

  constructor(session: GameSession, opts: LoopOptions) {
    this.session = session;
    this.opts = opts;
  }

  get active(): boolean { return this.running; }

  start(): void {
    if (this.running) return;
    this.running = true;
    this.abort = new AbortController();
    void this.run(this.abort.signal);
  }

  stop(): void {
    this.abort?.abort();
    this.abort = null;
    this.running = false;
  }

  private async run(signal: AbortSignal): Promise<void> {
    const content = this.opts.content;
    const neutral = this.opts.neutral ?? resolveDifficulty("normal");
    let stop: LoopStop = "aborted";

    while (!signal.aborted) {
      const state = this.session.getState();
      if (state.victory.over) { stop = "victory"; break; }
      if (this.stalls > STALL_LIMIT) { stop = "stalled"; break; }

      const nside = neutralSideToAct(state);
      let cmd: GameCommand | null = null;
      let actorIsHuman = false;
      let acting: PlayerId | null = null;

      if (nside !== null && state.battle !== null) {
        this.opts.onActor?.(null, "neutral");
        const only = enumerateNeutralBattleCommands(state, content)
          .filter((c): c is Extract<GameCommand, { type: "BATTLE" }> => c.type === "BATTLE")
          .map((c) => c.cmd);
        const pick = chooseBattleCommand(state.battle, nside, content, neutral, only);
        cmd = pick === null ? null : { type: "BATTLE", cmd: pick };
      } else {
        const actor = actorToAct(state);
        if (actor === null) { stop = "stalled"; break; }
        acting = actor;
        const controller = this.opts.controllers[actor as string];
        if (controller === undefined) { stop = "stalled"; break; }
        actorIsHuman = controller.kind === "human";
        this.opts.onActor?.(actor, actorIsHuman ? "human" : "ai");

        const obs = this.session.observeFor(actor);
        if (actorIsHuman) {
          cmd = await controller.chooseCommand(obs, signal);
        } else {
          const budget = this.budgeted(signal);
          try {
            cmd = await controller.chooseCommand(obs, budget.signal);
          } finally {
            budget.release();
          }
        }
      }

      if (signal.aborted) break;
      if (cmd === null) { stop = "stalled"; break; }

      const before = this.session.getState();
      const outcome = this.session.execute(cmd);

      if (!outcome.ok) {
        // A human's refusal is information: the status bar shows it and the
        // player tries again. A computer's is a bug, and spinning on it would
        // hang the game, so the turn is forced on exactly as the headless
        // runner does it.
        if (actorIsHuman) { await this.yieldToBrowser(); continue; }
        // The kernel accepts nothing but an answer while an interaction is
        // pending, so forcing END_TURN there is itself refused and the game
        // parks on a prompt nobody will ever press. Answer it with the first
        // response the kernel itself listed as legal, and force the turn on
        // only when there is no prompt in the way.
        const stuck = this.session.getState().pending;
        const forced = stuck !== null && stuck.playerId === acting && stuck.legalResponses.length > 0
          ? this.session.execute({
            type: "RESPOND_TO_INTERACTION", id: stuck.id, response: stuck.legalResponses[0]!,
          })
          : this.session.execute({ type: "END_TURN", player: before.turn.activePlayer });
        if (!forced.ok) { stop = "stalled"; break; }
      }

      const events = this.session.drainEvents();
      if (events.length > 0 && this.opts.present !== undefined) {
        await this.opts.present(events, this.session.getState());
      }

      const after = this.session.getState();
      const key = `${String(after.turn.month)}:${String(after.turn.week)}:${String(after.turn.day)}:${String(after.turn.activePlayer)}`;
      if (key === this.stallKey) this.stalls += 1;
      else { this.stallKey = key; this.stalls = 0; }

      // Yielding after every computer command costs a clamped 4 ms timer each
      // time, which is most of the wall clock of a long battle. Yield on a frame
      // budget instead: the page stays responsive and a hundred-command battle
      // round costs a handful of yields rather than a hundred.
      if (!actorIsHuman) await this.yieldIfDue();
    }

    this.running = false;
    this.opts.onActor?.(null, "none");
    this.opts.onStop?.(stop);
  }

  /** A computer player gets a wall clock as well as the loop's own signal, so a
   *  planner that goes long cannot freeze the page. The caller must `release()`
   *  once the planner returns: the relay listener sits on the game-lifetime
   *  signal, and one closure per AI command for a whole game is a leak. */
  private budgeted(parent: AbortSignal): { signal: AbortSignal; release: () => void } {
    const ms = this.opts.aiBudgetMs ?? 0;
    if (ms <= 0) return { signal: parent, release: () => {} };
    const ac = new AbortController();
    const timer = setTimeout(() => { ac.abort(); }, ms);
    const relay = (): void => { clearTimeout(timer); ac.abort(); };
    if (parent.aborted) relay();
    else parent.addEventListener("abort", relay, { once: true });
    const release = (): void => {
      clearTimeout(timer);
      parent.removeEventListener("abort", relay);
    };
    return { signal: ac.signal, release };
  }

  /** Hand the frame back so the page paints between computer commands. */
  private yieldToBrowser(): Promise<void> {
    return new Promise<void>((resolve) => { setTimeout(resolve, 0); });
  }

  private lastYield = 0;

  private async yieldIfDue(): Promise<void> {
    const now = Date.now();
    if (now - this.lastYield < FRAME_BUDGET_MS) return;
    this.lastYield = now;
    await this.yieldToBrowser();
  }
}
